// InspectionFormModal.jsx
//codex-- Track jobsite searches so prefilling a new form and closing/reopening cannot reuse stale results.
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Badge from './Badge';
import LoadingSpinner from './LoadingSpinner';

const InspectionFormModal = ({
  isOpen,
  onClose,
  formData,
  setFormData,
  onSubmit,
  technicians,
  handleDelete,
  isEditing,
  disabled,
  //codex-- Mapout opts into pending-form locking and notices; calendar callers retain their default loading behavior.
  loading,
  blockWhileLoading = false,
  loadingLabel = '',
  formNotice = null,
}) => {
  const [jobSiteQuery, setJobSiteQuery] = useState('');
  const [jobSiteResults, setJobSiteResults] = useState([]);
  const [isJobSiteDropdownVisible, setIsJobSiteDropdownVisible] = useState(false);
  //codex-- Cancel pending debounce callbacks and discard responses after a new query, selected site, or close.
  const searchState = useRef({ timeout: null, version: 0 });
  const apiUrl = process.env.REACT_APP_API_URL;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };


  //codex-- Display an explicitly prefilled new jobsite while retaining the existing edit-address fallback.
  useEffect(() => {
    const search = searchState.current;
    setJobSiteQuery(isOpen ? formData.job_site_name || (formData.id
      ? `${formData.job_site_address || ''}${formData.job_site_city ? ', ' + formData.job_site_city : ''}${formData.job_site_state ? ', ' + formData.job_site_state : ''}${formData.job_site_zip ? ' ' + formData.job_site_zip : ''}` : '') : '');
    setJobSiteResults([]);
    setIsJobSiteDropdownVisible(false);
    search.version++;
    clearTimeout(search.timeout);
    return () => { search.version++; clearTimeout(search.timeout); };
  }, [isOpen, formData.id, formData.job_site_name, formData.job_site_address, formData.job_site_city, formData.job_site_state, formData.job_site_zip]);
  
  const handleJobSiteInputChange = (e) => {
    const value = e.target.value;
    setJobSiteQuery(value);
    
    //codex-- Typing invalidates the old contract ID; selecting a search result is required before saving another jobsite.
    setFormData(prev => ({ ...prev, maintenance_id: '', next_due_date: '' }));
    setJobSiteResults([]);
    setIsJobSiteDropdownVisible(false);
    const search = searchState.current;
    clearTimeout(search.timeout);
    const version = ++search.version;

    if (value.length >= 3) {
      //codex-- Only publish results from the current query while this modal is still open.
      search.timeout = setTimeout(async () => {
        // Fetch job sites from the backend
        try {
          const response = await axios.get(`${apiUrl}/contracts_crud.php`, {
            params: { getJobSites: value },
          });
          if (version !== search.version) return;
          const results = Array.isArray(response.data) ? response.data : [];
          setJobSiteResults(results);
          setIsJobSiteDropdownVisible(true);
        } catch (error) {
          //codex-- Do not expose raw request details or errors from a closed/obsolete search.
          if (version === search.version) toast.error('Job sites could not be loaded. Please try again.');
        }
      }, 500); // Delay of 500ms

    } else {
      setJobSiteResults([]);
      setIsJobSiteDropdownVisible(false);
    }
  };

  const handleJobSiteSelect = (jobSite) => {
  //  let due = jobSite.next_due_date ? new Date(jobSite.next_due_date).toISOString().split('T')[0] : '';
    // let date = new Date().toISOString().split('T')[0]
    // if (jobSite.next_due_date < date) {
    //   toast.error('This job site is overdue for inspection', {
    //     position: 'top-center',
    //     autoClose: 5000,
    //     hideProgressBar: false,
    //     closeOnClick: true,
    //     pauseOnHover: true,
    //     draggable: true,
    //   });
    //   return;
    // }
    
    setJobSiteQuery(jobSite.job_site); // Display the selected job site's address
    setIsJobSiteDropdownVisible(false);
    setFormData((prevData) => ({
      ...prevData,
      maintenance_id: jobSite.maintenance_id,
      job_site_name: jobSite.job_site,
      next_due_date: jobSite.next_due_date,
    }));
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-20"
        //codex-- Mapout's pending-save mode also blocks Escape/backdrop dismissal.
        onClose={blockWhileLoading && loading ? () => {} : onClose}
      >
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        {/* Modal content */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <Dialog.Panel className="mx-auto w-full max-w-2xl overflow-hidden rounded-2xl bg-white/90 backdrop-blur-md shadow-xl ring-1 ring-gray-200">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-green-300 px-6 py-4 bg-green-500">
                  <Dialog.Title className="text-xl font-semibold text-gray-900">
                    {formData.id ? 'Edit Inspection' : 'New Inspection'}
                  </Dialog.Title>

                  {/* Optional status pill (show if you track status) */}
                  {formData?.status && (
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${
                        String(formData.status).toLowerCase() === 'completed'
                          ? 'bg-green-50 text-green-700 ring-green-600/20'
                          : 'bg-indigo-50 text-indigo-700 ring-indigo-600/20'
                      }`}
                    >
                      {formData.status}
                    </span>
                  )}
                </div>

                {/* Body (form) */}
                {/* //codex-- Name the shared form for accessible inspection creation and verification. */}
                <form id="inspection-form" aria-label="Inspection form" onSubmit={onSubmit} className="px-6 py-5 space-y-6">
                  {/* //codex-- Keep the existing form fields/layout, with optional pending-state locking for Mapout. */}
                  <fieldset disabled={blockWhileLoading && loading} className="min-w-0 space-y-6" aria-busy={blockWhileLoading && loading ? 'true' : undefined}>
                  {formNotice}

                  {/* Tentative flag */}
                  <div>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="tentative"
                        checked={!!formData.tentative}
                        onChange={handleInputChange}
                        className="h-4 w-4 rounded border-red-400 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm font-semibold text-red-600">Tentative</span>
                    </label>
                  </div>

                  {/* //codex-- Associate the tentative description label with its input. */}
                  {Boolean(formData.tentative) && (
                    <div>
                      <label htmlFor="inspection-status-description" className="block text-sm font-medium text-red-700">
                        Status Description (required when Tentative)
                      </label>
                      <textarea
                        id="inspection-status-description"
                        name="status_description"
                        value={formData.status_description || ''}
                        onChange={handleInputChange}
                        rows={3}
                        className="mt-1 block w-full rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-gray-900 focus:border-red-500 focus:ring-red-500"
                        placeholder="Explain why this inspection is tentative…"
                        required
                      />
                    </div>
                  )}

                  {/* //codex-- Keep the jobsite label accessible for both prefilled and searched selections. */}
                  <div>
                    <label htmlFor="inspection-job-site" className="block text-sm font-medium text-gray-700">Job Site</label>
                    <div className="relative">
                      <input
                        id="inspection-job-site"
                        type="text"
                        name="job_site"
                        //codex-- Use the synchronized query directly so prefilled sites can also be fully cleared.
                        value={jobSiteQuery}
                        onChange={handleJobSiteInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-3 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />

                      {/* Dropdown List */}
                      {isJobSiteDropdownVisible && jobSiteResults.length > 0 && (
                        <ul className="absolute z-10 mt-1 w-full max-h-60 overflow-auto divide-y divide-gray-100 rounded-lg border bg-white shadow-lg">
                          {jobSiteResults.map((jobSite) => {
                            const liBgClass =
                              jobSite.status === 0
                                ? 'bg-red-50 hover:bg-red-100 text-red-700'
                                : 'hover:bg-indigo-50';
                            return (
                              <li
                                key={jobSite.maintenance_id}
                                className={`px-4 py-2 cursor-pointer ${liBgClass}`}
                                onClick={() => handleJobSiteSelect(jobSite)}
                              >
                                {jobSite.status === 0 && (
                                  <div className="text-red-700 p-1 text-xs font-semibold">
                                    ********** Inactive Contract **********
                                  </div>
                                )}
                                {jobSite.contract_hold_amount && (
                                  <div className="text-red-700 p-1 text-xs font-semibold">
                                    ********** Contract On Hold **********
                                  </div>
                                )}
                                <div className="text-sm font-medium text-gray-900">
                                  <span className="inline-block rounded bg-yellow-50 px-1.5 py-0.5">
                                    ID:{jobSite.id} | {jobSite.customer_name}
                                  </span>{' '}
                                  | {jobSite.contract_type} | {jobSite.system_type}
                                </div>
                                <div className="font-medium text-gray-800">{jobSite.job_site}</div>
                                <div className="text-xs text-green-600">
                                  Next Inspection Due: {jobSite.next_due_date}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* //codex-- Label the existing editable schedule controls without changing calendar field names. */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="inspection-date-from" className="block text-sm font-medium text-gray-700">Schedule Date</label>
                      <input
                        id="inspection-date-from"
                        type="date"
                        name="scheduled_due_date"
                        value={formData.scheduled_due_date}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="inspection-time-in" className="block text-sm font-medium text-gray-700">Time In</label>
                      <input
                        id="inspection-time-in"
                        type="time"
                        name="schedule_time_in"
                        value={formData.schedule_time_in}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="inspection-date-to" className="block text-sm font-medium text-gray-700">End Date</label>
                      <input
                        id="inspection-date-to"
                        type="date"
                        name="schedule_date_to"
                        value={
                          formData.schedule_date_to
                            ? formData.schedule_date_to
                            : formData.scheduled_due_date || new Date().toISOString().split('T')[0]
                        }
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="inspection-time-out" className="block text-sm font-medium text-gray-700">Time Out</label>
                      <input
                        id="inspection-time-out"
                        type="time"
                        name="schedule_time_out"
                        value={formData.schedule_time_out}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  {/* //codex-- Associate technician labels with the reused selects. */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="inspection-lead-tech" className="block text-sm font-medium text-gray-700">Lead Technician</label>
                      <select
                        id="inspection-lead-tech"
                        name="lead_tech_id"
                        value={formData.lead_tech_id || ''}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        <option value="">Select Technician</option>
                        {technicians.map((tech) => (
                          <option key={tech.id} value={tech.id}>
                            {tech.first_name} {tech.last_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="inspection-additional-tech" className="block text-sm font-medium text-gray-700">Additional Technician</label>
                      <select
                        id="inspection-additional-tech"
                        name="additional_tech_id"
                        value={formData.additional_tech_id}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        <option value="">Additional Tech</option>
                        {technicians.map((tech) => (
                          <option key={tech.id} value={tech.id}>
                            {tech.first_name} {tech.last_name}
                          </option>
                        ))}
                      </select>

                      {/* Same-tech warning */}
                      {formData.lead_tech_id &&
                        formData.additional_tech_id &&
                        parseInt(formData.lead_tech_id) === parseInt(formData.additional_tech_id) && (
                          <p className="mt-1 text-xs font-medium text-red-600">
                            Lead Tech and Additional Tech cannot be the same person.
                          </p>
                        )}
                    </div>
                  </div>

                  {/* //codex-- Keep inspection notes editable with an associated label. */}
                  <div>
                    <label htmlFor="inspection-notes" className="block text-sm font-medium text-gray-700">Inspection Notes</label>
                    <textarea
                      id="inspection-notes"
                      name="inspection_notes"
                      value={formData.inspection_notes}
                      onChange={handleInputChange}
                      rows={4}
                      className="mt-1 block w-full rounded-lg border border-gray-300 p-3 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>

                  {/* //codex-- Expose Mapout's loading reason while keeping the calendar's existing spinner available. */}
                  {loading ? <div role="status"><LoadingSpinner>{loadingLabel}</LoadingSpinner></div> : (
                  <>
                  {/* Footer (inside form so submit works) */}
                  <div className="mt-2 flex items-center justify-end gap-2 border-t border-gray-300  -mx-6 px-6 pt-4">
                    {isEditing && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const idForDelete = formData?.id || formData?.inspection_id;
                          handleDelete?.(idForDelete);
                        }}
                        className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        Delete
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={onClose}
                      className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {formData.id ? 'Update' : 'Create'}
                    </button>
                  </div>                  
                  
                  </>                    
                  )}
                  </fieldset>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default InspectionFormModal;
